import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
// import { Inter } from '@fontsource/inter';
import JarManagement from './JarManagement';
import SubheadingManagement from './SubheadingManagement';
import LiveSessionManagement from './LiveSessionManagement';
import IndustryImageManagement from './IndustryImageManagement';
import DashboardOverview from './DashboardOverview';
import StudentVerification from './StudentVerification';
import IPManagement from './IPManagement';
import AdminAuth from './AdminAuth';
import Inquiries from './pages/Inquiries';
import FeedbackAdmin from './pages/FeedbackAdmin';
import ChatbotManagement from './components/ChatbotManagement';
import ExpertManagement from './components/ExpertManagement';
import AdvisoryManagement from './components/advisory/AdvisoryManagement';
import AdvisoryCategories from './components/advisory/AdvisoryCategories';
import BlogManagement from './BlogManagement';
import LatestUpdateManagement from './LatestUpdateManagement';
import CollegeManagement from './CollegeManagement';
import UniversityManagement from './UniversityManagement';
import LearningResourcesManagement from './LearningResourcesManagement';
import { jwtDecode } from 'jwt-decode';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import theme from './theme';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const AdminLayout = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background: ${theme.colors.background};
  position: relative;
  z-index: 1;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: ${props => props.success ? '#DCFCE7' : '#FEE2E2'};
  color: ${props => props.success ? '#166534' : '#991B1B'};
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${props => props.success ? '#86EFAC' : '#FECACA'};
`;

const CloseMessage = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);
  const [unverifiedStudentCount, setUnverifiedStudentCount] = useState(0);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const intervalRef = useRef(null); // Add ref to track interval

  const fetchUnresolvedCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/inquiries`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const inquiries = data.data || [];
        const unresolved = inquiries.filter(inquiry => 
          inquiry.status !== 'resolved'
        ).length;
        setUnresolvedCount(unresolved);
      }
    } catch (error) {
      console.error('Error fetching unresolved count:', error);
    }
  }, []);

  const fetchPendingFeedbackCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/feedback/statistics`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const statistics = data.data || {};
        const pendingCount = statistics.pending || 0;
        setPendingFeedbackCount(pendingCount);
      } else {
        console.error('Failed to fetch feedback statistics - Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching pending feedback count:', error);
    }
  }, []);

  const fetchUnverifiedStudentCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const students = data.data || [];
        const unverifiedCount = students.filter(student => 
          student.verification_status === 'pending' || student.verification_status === 'unverified'
        ).length;
        setUnverifiedStudentCount(unverifiedCount);
      } else {
        console.error('Failed to fetch students - Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching unverified student count:', error);
    }
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        fetchUnresolvedCount(); // Fetch unresolved count when authenticated
        fetchPendingFeedbackCount(); // Fetch pending feedback count when authenticated
        fetchUnverifiedStudentCount(); // Fetch unverified student count when authenticated
        
        // Clear any existing interval before setting a new one
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        // Set up periodic updates every 10 seconds
        intervalRef.current = setInterval(() => {
          fetchUnresolvedCount();
          fetchPendingFeedbackCount();
          fetchUnverifiedStudentCount();
        }, 10000); // 10 seconds
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      // If backend is not available, check if token exists and is not expired
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp > currentTime) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
        }
      } catch (decodeError) {
        localStorage.removeItem('adminToken');
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchUnresolvedCount, fetchPendingFeedbackCount, fetchUnverifiedStudentCount]);

  useEffect(() => {
    // Check for existing auth token
    const token = localStorage.getItem('adminToken');
    
    if (token) {
      // First check client-side if token is expired
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp <= currentTime) {
          localStorage.removeItem('adminToken');
          setIsLoading(false);
          return;
        }
        
        // Verify token with backend
        verifyToken(token);
      } catch (decodeError) {
        localStorage.removeItem('adminToken');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  // Update unresolved count when switching to inquiries tab
  useEffect(() => {
    if (isAuthenticated && activeTab === 'inquiries') {
      fetchUnresolvedCount();
    }
  }, [activeTab, isAuthenticated, fetchUnresolvedCount]);

  // Update pending feedback count when switching to feedback tab
  useEffect(() => {
    if (isAuthenticated && activeTab === 'feedback') {
      fetchPendingFeedbackCount();
    }
  }, [activeTab, isAuthenticated, fetchPendingFeedbackCount]);

  // Update unverified student count when switching to students tab
  useEffect(() => {
    if (isAuthenticated && activeTab === 'students') {
      fetchUnverifiedStudentCount();
    }
  }, [activeTab, isAuthenticated, fetchUnverifiedStudentCount]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAuthenticated(true);
    showMessage('Login successful', 'success');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setActiveTab('overview');
    
    // Clear interval on logout
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    showMessage('Logged out successfully', 'success');
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  if (isLoading) {
    return (
      <AdminContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.8)'
        }}>
          Loading...
        </div>
      </AdminContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <AdminContainer>
        <AdminAuth onLogin={handleLogin} />
      </AdminContainer>
    );
  }

  const getBreadcrumbs = () => {
    const breadcrumbMap = {
      'overview': ['Dashboard'],
      'students': ['Management', 'Student Verification'],
      'colleges': ['Management', 'Colleges'],
      'universities': ['Management', 'Universities'],
      'ip-management': ['Management', 'IP Management'],
      'live-sessions': ['Management', 'Live Sessions'],
      'industry-images': ['Management', 'Industry Images'],
      'inquiries': ['Communication', 'Inquiries'],
      'feedback': ['Communication', 'Feedback'],
      'chatbot': ['Communication', 'Chatbot'],
      'experts': ['Communication', 'Experts'],
      'advisory-members': ['Advisory', 'Members'],
      'advisory-categories': ['Advisory', 'Categories'],
      'blogs': ['Content', 'Blogs'],
      'latest-updates': ['Content', 'Latest Updates'],
      'jars': ['Content', 'Jars'],
      'subheadings': ['Content', 'Subheadings'],
      'learning-resources': ['Content', 'Learning Resources']
    };
    return breadcrumbMap[activeTab] || ['Dashboard'];
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview showMessage={showMessage} />;
      case 'jars':
        return <JarManagement showMessage={showMessage} />;
      case 'subheadings':
        return <SubheadingManagement showMessage={showMessage} />;
      case 'live-sessions':
        return <LiveSessionManagement showMessage={showMessage} />;
      case 'industry-images':
        return <IndustryImageManagement showMessage={showMessage} />;
      case 'students':
        return <StudentVerification showMessage={showMessage} />;
      case 'ip-management':
        return <IPManagement showMessage={showMessage} />;
      case 'inquiries':
        return <Inquiries showMessage={showMessage} />;
      case 'feedback':
        return <FeedbackAdmin showMessage={showMessage} onFeedbackUpdate={fetchPendingFeedbackCount} />;
      case 'chatbot':
        return <ChatbotManagement showMessage={showMessage} />;
      case 'experts':
        return <ExpertManagement showMessage={showMessage} />;
      case 'advisory-members':
        return <AdvisoryManagement showMessage={showMessage} />;
      case 'advisory-categories':
        return <AdvisoryCategories showMessage={showMessage} />;
      case 'blogs':
        return <BlogManagement showMessage={showMessage} />;
      case 'latest-updates':
        return <LatestUpdateManagement showMessage={showMessage} />;
      case 'colleges':
        return <CollegeManagement showMessage={showMessage} />;
      case 'universities':
        return <UniversityManagement showMessage={showMessage} />;
      case 'learning-resources':
        return <LearningResourcesManagement showMessage={showMessage} token={localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken')} />;
      default:
        return <DashboardOverview showMessage={showMessage} />;
    }
  };

  return (
    <AdminContainer>
      <AdminLayout>
        <Sidebar
          activeTab={activeTab}
          onNavigate={setActiveTab}
          onLogout={handleLogout}
          counts={{
            unverifiedStudentCount,
            unresolvedCount,
            pendingFeedbackCount
          }}
          isMobileOpen={isMobileOpen}
          onMobileClose={() => setIsMobileOpen(false)}
        />
        
        <MainContent>
          <Header breadcrumbs={getBreadcrumbs()} />
          
          <ContentArea>
            {message && (
              <Message success={messageType === 'success'}>
                {message}
                <CloseMessage onClick={() => setMessage('')}>×</CloseMessage>
              </Message>
            )}

            {renderContent()}
          </ContentArea>
        </MainContent>
      </AdminLayout>
    </AdminContainer>
  );
}

export default AdminDashboard;
